import React from 'react';

import clsx from 'clsx';

import { CheckedIcon, StepDivider } from 'assets/images';
import { ProgressBarStatus } from 'enums/ProgressBarStatus';
import { Theme } from 'enums/Theme';
import useMobileCheck from 'hooks/useMobileCheck';

import styles from './FormHeader.module.scss';

interface FormHeaderProps {
  title?: string | JSX.Element;
  theme?: Theme;
  isBorderRounded?: boolean;
  subtitle?: string;
  percent?: number | ProgressBarStatus;
  classNameWrapper?: string;
  classNameTitle?: string;
  classNameHeader?: string;
  step?: number;
  totalSteps?: number;
}

const FormHeader = ({
  title,
  theme = Theme.Light,
  isBorderRounded = true,
  subtitle,
  percent,
  classNameWrapper,
  classNameTitle,
  classNameHeader,
  step,
  totalSteps = 4,
  children,
}: React.PropsWithChildren<FormHeaderProps>) => {
  const isMobile = useMobileCheck();
  const renderTextProgressBarValue = (percentValue: number | ProgressBarStatus | undefined) => {
    switch (percentValue) {
      case ProgressBarStatus.Done:
        return (
          <div className={styles.checked}>
            <CheckedIcon className={styles.imageIcon} />
          </div>
        );
      case ProgressBarStatus.Failed:
        return <p className={styles.checked}>i</p>;
      case ProgressBarStatus.Warning:
        return <p className={styles.checkedWarning}>!</p>;
      case ProgressBarStatus.Error:
        return <p className={styles.checkedError}>!</p>;
      default:
        return null;
    }
  };

  const renderStepIndicator = () => {
    if (!step || !totalSteps) {
      return null;
    }

    return (
      <div className={styles.stepIndicator}>
        {!isMobile && <span className={styles.stepLabel}>Step:</span>}
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < step;
          const isCurrent = stepNumber === step;

          return (
            <React.Fragment key={stepNumber}>
              <div
                className={clsx(styles.stepCircle, {
                  [styles.stepCompleted]: isCompleted,
                  [styles.stepCurrent]: isCurrent,
                  [styles.stepUpcoming]: stepNumber > step,
                })}
              >
                {isCompleted ? <CheckedIcon className={styles.stepCheckIcon} /> : <span>{stepNumber}</span>}
              </div>
              {isMobile && stepNumber !== totalSteps && <StepDivider />}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className={clsx(styles.headerWrapper, classNameWrapper)}>
      <div
        className={clsx(
          {
            [styles.header]: isBorderRounded,
            [styles.headerNotRounded]: !isBorderRounded,
            [styles.headerDarkTheme]: theme === Theme.Dark,
          },
          classNameHeader,
        )}
      >
        {children || (
          <>
            <div className={styles.titlePercentBlock}>
              <div
                className={clsx(
                  styles.titles,
                  theme === Theme.Dark && styles.titlesDarkTheme,
                  !percent && styles.titlesFullWidth,
                  classNameTitle,
                )}
              >
                <h1 className={styles.headerTitle}>{title}</h1>
                {subtitle && <p>{subtitle}</p>}
              </div>
              {typeof percent === 'number' && isMobile && <span className={styles.percentText}>{percent}%</span>}
            </div>
            <div className={styles.rightSection}>
              {renderStepIndicator()}
              {typeof percent === 'number'
                ? !isMobile && <span className={styles.percentText}>{percent}%</span>
                : renderTextProgressBarValue(percent)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FormHeader;
